import { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Btn, Badge } from "./UI";
import { SlidePanel } from "./SlidePanel";
import { FLabel, FSelect } from "../pages/engagement/FormControls";
import { DateField } from "../pages/engagement/DateField";
import { CalendarCheck, LogOut, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const DISCHARGE_TYPES = ["Completed treatment", "Successful completion", "Transferred to another provider", "Against medical advice (AMA)", "Administrative discharge", "Lost contact", "Other"];

export function ClientQuickDrawer({ client, open, onClose, onUpdated }) {
  const { user } = useAuth();
  const clinicalEditable = canEdit(user, "clinical");
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [dType, setDType] = useState(DISCHARGE_TYPES[0]);
  const [dDate, setDDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [dSummary, setDSummary] = useState("");
  const [armed, setArmed] = useState(false);
  const [discharging, setDischarging] = useState(false);

  const load = useCallback(() => {
    if (!client) return;
    api.get(`/clients/${client.id}/quick-status`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [client]);
  useEffect(() => {
    if (open) { setData(null); setArmed(false); setDType(DISCHARGE_TYPES[0]); setDDate(dayjs().format("YYYY-MM-DD")); setDSummary(""); load(); }
  }, [open, load]);

  const checkIn = async (a) => {
    setBusyId(a.id);
    try {
      await api.post(`/appointments/${a.id}/check-in`);
      toast.success(`Checked in for ${a.type || "appointment"}`);
      load(); onUpdated && onUpdated();
    } catch (e) { toast.error(apiError(e)); }
    setBusyId(null);
  };

  const discharge = async () => {
    setDischarging(true);
    try {
      await api.post(`/clients/${client.id}/quick-discharge`, { discharge_date: dDate, discharge_type: dType, discharge_summary: dSummary });
      toast.success("Client discharged");
      setArmed(false); load(); onUpdated && onUpdated();
    } catch (e) { toast.error(apiError(e)); }
    setDischarging(false);
  };

  if (!client) return null;
  const appts = data?.today_appointments || [];
  const adm = data?.active_admission;

  return (
    <SlidePanel open={open} onClose={onClose} title="Check In / Discharge" subtitle={`${client.first_name} ${client.last_name}`} testId="client-quick-drawer">
      {!data ? <div className="py-10 text-center text-sm text-[#84675B]">Loading…</div> : (
        <div className="space-y-6">
          <section>
            <h4 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#84675B]"><CalendarCheck size={13} /> Today's Appointments ({dayjs().format("MMM D")})</h4>
            {appts.length === 0 ? (
              <p className="rounded-xl border border-dashed border-[#E5DFD3] px-4 py-3 text-sm text-[#84675B]" data-testid="quick-no-appointments">No appointments scheduled today.</p>
            ) : (
              <div className="space-y-2">
                {appts.map((a) => (
                  <div key={a.id} data-testid={`quick-appt-${a.id}`} className="flex items-center gap-3 rounded-xl border border-[#EFEBE0] px-4 py-3">
                    <Clock size={14} className="text-[#7B6157] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-[#3E2723]">{a.start_time || "--:--"} · {a.type || "Appointment"}</div>
                      <div className="text-[11px] text-[#84675B]">{a.clinician_name || ""}</div>
                    </div>
                    {a.status === "checked_in" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] px-2.5 py-1 text-[10px] font-bold text-[#2E7D32]" data-testid={`quick-checked-in-${a.id}`}><CheckCircle2 size={11} /> CHECKED IN</span>
                    ) : ["scheduled", "confirmed"].includes(a.status) ? (
                      <Btn data-testid={`quick-checkin-${a.id}`} className="rounded-lg px-3 py-1.5 text-xs" disabled={busyId === a.id} onClick={() => checkIn(a)}>
                        {busyId === a.id ? "…" : "Check In"}
                      </Btn>
                    ) : <Badge value={a.status} />}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h4 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#84675B]"><LogOut size={13} /> Admission & Discharge</h4>
            {!adm ? (
              <div className="rounded-xl border border-dashed border-[#E5DFD3] px-4 py-3 text-sm text-[#84675B]" data-testid="quick-no-admission">
                No active admission.{data?.last_admission?.discharge_date ? ` Last: ${data.last_admission.program || "-"} discharged ${dayjs(data.last_admission.discharge_date).format("MM/DD/YYYY")}.` : ""}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-xl bg-[#F5F2EB] px-4 py-3" data-testid="quick-active-admission">
                  <div className="text-sm font-bold text-[#3E2723]">{adm.program || "Program not set"}</div>
                  <div className="text-[11px] text-[#7B6157]">Admitted {adm.admission_date ? dayjs(adm.admission_date).format("MM/DD/YYYY") : "-"}{adm.level_of_care ? ` · ${adm.level_of_care}` : ""}</div>
                </div>
                {clinicalEditable ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div><FLabel>Discharge type</FLabel>
                        <FSelect data-testid="quick-discharge-type" value={dType} onChange={(e) => setDType(e.target.value)}>{DISCHARGE_TYPES.map((t) => <option key={t}>{t}</option>)}</FSelect></div>
                      <div><FLabel>Effective date</FLabel><DateField testId="quick-discharge-date" value={dDate} onChange={setDDate} noFuture /></div>
                    </div>
                    <div><FLabel>Discharge summary (optional)</FLabel>
                      <textarea data-testid="quick-discharge-summary" value={dSummary} onChange={(e) => setDSummary(e.target.value)} rows={2}
                        className="w-full rounded-xl border border-[#E5DFD3] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8D6E63]" /></div>
                    {!armed ? (
                      <Btn data-testid="quick-discharge-btn" variant="outline" className="w-full rounded-xl border-[#C62828] text-[#C62828] hover:bg-[#FFEBEE]" onClick={() => setArmed(true)}>
                        Discharge Client
                      </Btn>
                    ) : (
                      <div className="flex items-center gap-2 rounded-xl border border-[#C62828] bg-[#FFEBEE] px-3 py-2.5">
                        <span className="flex-1 text-xs font-bold text-[#C62828]">Discharge {client.first_name} effective {dayjs(dDate).format("MM/DD/YYYY")}?</span>
                        <Btn data-testid="quick-discharge-confirm" className="rounded-lg px-3 py-1.5 text-xs" disabled={discharging} onClick={discharge}>{discharging ? "…" : "Confirm"}</Btn>
                        <Btn variant="ghost" className="rounded-lg px-3 py-1.5 text-xs" onClick={() => setArmed(false)}>Cancel</Btn>
                      </div>
                    )}
                    <p className="text-[10px] text-[#84675B]">Discharging closes all active admission records, sets the client status to discharged, notifies the assigned clinician, and is audit-logged.</p>
                  </>
                ) : <p className="rounded-lg bg-[#FFF8E1] border border-[#FFE0B2] px-3 py-2 text-[11px] text-[#6D4C41]">Your role has read-only clinical access, so discharging is unavailable.</p>}
              </div>
            )}
          </section>
        </div>
      )}
    </SlidePanel>
  );
}
